import { useCallback, useSyncExternalStore } from "react";

/**
 * Subscribe to a CSS media query and re-render when its match state flips.
 *
 * Built on `useSyncExternalStore` rather than `useState` + `useEffect` so the
 * first render already reflects the real match state (no false initial value
 * that corrects itself a frame later) and no change is missed in the gap
 * between render and effect.
 *
 * Defensive by default: when `window.matchMedia` is unavailable - SSR, or a
 * jsdom test that hasn't stubbed it - the query is treated as unmatched rather
 * than throwing. Real browsers always provide it.
 *
 * @param query a media query string, e.g. `"(min-width: 1024px)"`
 * @returns whether the query currently matches
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (typeof window === "undefined" || !window.matchMedia) {
        return () => {};
      }
      const mql = window.matchMedia(query);
      mql.addEventListener("change", onStoreChange);
      return () => mql.removeEventListener("change", onStoreChange);
    },
    [query]
  );

  const getSnapshot = () => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return false;
    }
    return window.matchMedia(query).matches;
  };

  // The server can't know the viewport, so it renders the unmatched branch and
  // the client hydrates to the real value. This app is a pure SPA today, but
  // keeping the parameter makes the hook safe to reuse if that ever changes.
  const getServerSnapshot = () => false;

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
